import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CounterMode } from '../../types/viewer.types';
import { Label, NewCustomLabel } from '../../types/protocol.types';
import { StatsMode } from '../stats-settings/stats-settings.component';
import { IAssetItem } from '@analysis/domain';
import { FindingParameters, StepTask } from '@telespot/domain';
import { Category, Pipeline, Option, Step, ProtocolScope } from '@telespot/protocols/data-access';

import { AssetDataField } from '@telespot/sdk';

/** Analyzer pipeline row includes category ids (extends store Pipeline). */
type ProtocolAnalysisPipeline = Pipeline & { categories: string[] };

@Component({
  selector: 'ts-sample-analysis-panel',
  templateUrl: './sample-analysis-panel.component.html',
  styleUrls: ['./sample-analysis-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SampleAnalysisPanelComponent implements OnChanges {
  @Input() public readonly dataEditable: boolean;
  @Input() public readonly taggingProtocol: boolean;
  @Input() public readonly segmentation: boolean;
  @Input() public readonly multiSelection: boolean;
  @Input() public readonly canEdit: boolean;
  @Input() public readonly loadingProtocol: boolean;
  @Input() public readonly loadingAnalysis: boolean;
  @Input() public readonly showPinned: boolean;
  @Input() public readonly acquisitionEdition: boolean;
  @Input() public readonly stitchingScans: string;
  @Input() public readonly selectedAsset: IAssetItem;
  @Input() public readonly sampleId: string;
  @Input() public readonly analysisContext: ProtocolScope;
  @Input() public readonly selectedLabels: Label[];
  @Input() public readonly visibleLabels: string[];
  @Input() public readonly pinnedLabels: Set<string>;
  @Input() public readonly excludedFromCounter: Set<string>;
  @Input() public readonly collapsedCategories: Set<string>;
  @Input() public readonly currentAnalyst: any;
  @Input() public readonly pipelines: Pipeline[];
  /** Protocol store shape (same as ts-protocol-panel). */
  @Input() public readonly categories: Record<string, Category> = {};
  @Input() public readonly options: Record<string, Option> = {};
  @Input() public readonly steps: Step[];
  @Input() public readonly acquisitionFields: AssetDataField[];
  @Input() public readonly activeFindings: any[];
  @Input() public readonly labelCount: Map<string, number> = new Map();
  @Input() public readonly sampleLabelCount: Map<string, number> = new Map();
  @Input() public readonly counterMode: CounterMode = CounterMode.counter;
  @Input() public readonly totalCount: number;
  @Input() public readonly totalActiveAssetRoiCount: number = 0;
  @Input() public readonly assetCategoryCounts: Map<string, number> = new Map();
  @Input() public readonly totalObjectsCount: number | null = null;
  @Input() public readonly statsMode: StatsMode = StatsMode.COMPLETE;
  @Input() public readonly hasSelectedRois: boolean = false;
  @Input() public readonly labelsInAllSelectedRois: Set<string> = new Set();
  @Input() public readonly labelsInSomeSelectedRois: Set<string> = new Set();
  @Input() public readonly forceExpanded: boolean = false;
  /** Compact mode: collapse the panel to a colours+numbers rail (label text and
   *  the wide controls are hidden). Driven by the microscope layout state. */
  @Input() public readonly collapsed: boolean = false;

  @Output() public readonly toogleMode = new EventEmitter<ProtocolScope>();
  @Output() public readonly toggleMulti = new EventEmitter<void>();
  @Output() public readonly tooglePinned = new EventEmitter<void>();
  @Output() public readonly setStatsMode = new EventEmitter<StatsMode>();
  @Output() public readonly saveAssetData = new EventEmitter<void>();
  @Output() public readonly discardDataChanges = new EventEmitter<void>();
  @Output() public readonly acquisitionEditionMode = new EventEmitter<boolean>();
  @Output() public readonly toogleTaskPanel = new EventEmitter<string>();
  @Output() public readonly selectLabel = new EventEmitter<string>();
  @Output() public readonly newLabel = new EventEmitter<Partial<NewCustomLabel>>();
  @Output() public readonly pinLabel = new EventEmitter<string>();
  @Output() public readonly counterLabel = new EventEmitter<string>();
  @Output() public readonly visibleLabel = new EventEmitter<string>();
  @Output() public readonly acquisitionFieldChange = new EventEmitter<{ fieldPath: string; value: any }>();
  @Output() public readonly createFinding = new EventEmitter<any>();
  @Output() public readonly updateFinding = new EventEmitter<any>();
  /** Emits a label id after the cursor rests on it (same delay as the
   *  tooltip), null when the cursor leaves. Drives ROI highlighting. */
  @Output() public readonly hoverLabel = new EventEmitter<string | null>();

  public expanded = true;
  public Context = ProtocolScope;
  public CounterMode = CounterMode;
  public StatsMode = StatsMode;

  counterModes = Object.values(CounterMode);

  /** Copy of categories without TEXT/OCR synthetic options (lookups). */
  protocolCategoriesById: Record<string, Category> = {};
  protocolOptionsById: Record<string, Option> = {};
  /** Augmented for ts-protocol-panel (synthetic row for TEXT/OCR when options are empty). */
  protocolPanelDisplayCategoriesById: Record<string, Category> = {};
  protocolPanelDisplayOptionsById: Record<string, Option> = {};

  private readonly analysisTypeProtocolPipelinesById = new Map<string, ProtocolAnalysisPipeline[]>();
  private static readonly HOVER_DELAY_MS = 1000;
  private hoverTimer?: ReturnType<typeof setTimeout>;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['categories'] || changes['options']) {
      this.rebuildProtocolPanelMaps();
    }
    if (changes['pipelines']) {
      this.analysisTypeProtocolPipelinesById.clear();
    }

    if (changes['forceExpanded'] && this.forceExpanded) {
      this.expanded = true;
    }

    if (
      changes['hasSelectedRois'] &&
      this.hasSelectedRois &&
      this.forceExpanded &&
      (!changes['hasSelectedRois'].previousValue || changes['hasSelectedRois'].previousValue === false)
    ) {
      this.expanded = true;
    }
  }

  onToggleContext(context: ProtocolScope): void {
    if (this.analysisContext === context) return;
    this.toogleMode.emit(context);
    this.expanded = true;
  }

  onCreateFinding(info: any) {
    const activeElement = document.activeElement as HTMLElement;

    const shouldCreate = this.canEdit && !this.loadingAnalysis;

    if (!shouldCreate) return;

    this.createFinding.emit(info);

    // REVIEW: Needed to avoid loss of focus on input textarea
    setTimeout(() => {
      activeElement.focus();
    }, 0);
  }

  onUpdateFinding(info: any) {
    const activeElement = document.activeElement as HTMLElement;

    this.updateFinding.emit(info);

    // REVIEW: Needed to avoid loss of focus on input textarea
    setTimeout(() => {
      activeElement.focus();
    }, 0);
  }

  getActiveFinding(stepId: string): FindingParameters | null {
    return this.activeFindings?.find((finding: FindingParameters) => finding.pipelineStepId === stepId) || null;
  }

  trackByPipelineId(index: number, pipeline: any): string {
    return pipeline.id;
  }

  isTaggingTask(type: StepTask) {
    return type === StepTask.ROIDETECTION || type === StepTask.POSITION || type === StepTask.ROITRACKING;
  }

  hasTaggingCategories(pipeline: { categories: string[] }): boolean {
    return pipeline.categories.some(
      (categoryId) =>
        this.protocolCategoriesById[categoryId] && this.isTaggingTask(this.protocolCategoriesById[categoryId].stepTask)
    );
  }

  onAcquisitionFieldChange(fieldChange: { fieldPath: string; value: any }) {
    this.acquisitionFieldChange.emit(fieldChange);
  }

  private rebuildProtocolPanelMaps(): void {
    this.protocolCategoriesById = { ...(this.categories ?? {}) };
    this.protocolOptionsById = { ...(this.options ?? {}) };

    const dispCats: Record<string, Category> = { ...this.protocolCategoriesById };
    const dispOpts: Record<string, Option> = { ...this.protocolOptionsById };

    for (const catId of Object.keys(dispCats)) {
      const cat = dispCats[catId];
      const task = cat.stepTask as StepTask;
      if ((task === StepTask.TEXT || task === StepTask.OCR) && (!cat.options || cat.options.length === 0)) {
        const sid = this.syntheticTextOptionId(catId);
        dispCats[catId] = { ...cat, options: [sid] };
        dispOpts[sid] = { id: sid, name: '', threshold: 0, color: '#888888' };
      }
    }

    this.protocolPanelDisplayCategoriesById = dispCats;
    this.protocolPanelDisplayOptionsById = dispOpts;
  }

  syntheticTextOptionId(categoryId: string): string {
    return `__sample_analysis_text__${categoryId}`;
  }

  isSyntheticProtocolOption(optionId: string): boolean {
    return optionId.startsWith('__sample_analysis_text__');
  }

  isSegmentationTask(task: StepTask | string): boolean {
    return task === StepTask.SEGMENTATION || task === 'segmentation';
  }

  protocolSectionKind(category: Category): 'tagging' | 'segmentation' | 'analysis' {
    if (this.isTaggingTask(category.stepTask as StepTask)) return 'tagging';
    if (this.isSegmentationTask(category.stepTask)) return 'segmentation';
    return 'analysis';
  }

  protocolLabelKind(category: Category): 'tagging' | 'segmentation' | 'analysis' {
    return this.protocolSectionKind(category);
  }

  /** Single row hosts full ts-analysis-input; other classification rows are hidden placeholders. */
  isAnalysisInputAnchorRow(category: Category, labelId: string): boolean {
    const task = category.stepTask as StepTask;
    if (task === StepTask.TEXT || task === StepTask.OCR) {
      return labelId === this.syntheticTextOptionId(category.id);
    }
    if (task === StepTask.CLASSIFICATION) {
      return !!category.options?.length && labelId === category.options[0];
    }
    return false;
  }

  isProtocolRowPlaceholder(category: Category, labelId: string): boolean {
    if ((category.stepTask as StepTask) === StepTask.CLASSIFICATION && (category.options?.length ?? 0) > 1) {
      return labelId !== category.options[0];
    }
    return false;
  }

  /** Non–analysis-input tasks under analysis bucket: show once on first option row. */
  isProtocolAnalysisFallbackRow(category: Category, labelId: string): boolean {
    if (this.protocolLabelKind(category) !== 'analysis') return false;
    if (this.isAnalysisInputAnchorRow(category, labelId)) return false;
    if (this.isProtocolRowPlaceholder(category, labelId)) return false;
    if (!category.options?.length) return true;
    return labelId === category.options[0];
  }

  isProtocolAnalysisSkipOnlyRow(category: Category, labelId: string): boolean {
    if (this.protocolLabelKind(category) !== 'analysis') return false;
    if (this.isAnalysisInputAnchorRow(category, labelId)) return false;
    if (this.isProtocolRowPlaceholder(category, labelId)) return false;
    if (!category.options?.length) return false;
    return labelId !== category.options[0];
  }

  isSegmentationLabelChecked(optionId: string): boolean {
    return this.selectedLabels.some((l) => {
      const key = 'id' in l ? (l as Label & { id: string }).id : l.uuid;
      return key === optionId;
    });
  }

  asAnalysisTypeProtocolPipelines(pipeline: ProtocolAnalysisPipeline): ProtocolAnalysisPipeline[] {
    let hit = this.analysisTypeProtocolPipelinesById.get(pipeline.id);
    if (!hit) {
      hit = [pipeline];
      this.analysisTypeProtocolPipelinesById.set(pipeline.id, hit);
    } else {
      hit[0] = pipeline;
    }
    return hit;
  }

  getPipelineIdForCategory(categoryId: string): string {
    const list = this.pipelines as ProtocolAnalysisPipeline[];
    return list?.find((p) => p.categories.includes(categoryId))?.id ?? '';
  }

  onUnifiedProtocolLabelSelected(label: Option): void {
    if (this.isSyntheticProtocolOption(label.id)) return;

    const category = this.findCategoryContainingOption(label.id);
    if (!category) return;

    if (this.isTaggingTask(category.stepTask as StepTask)) {
      this.onRoiProtocolLabelSelected(label);
      return;
    }

    if (this.isSegmentationTask(category.stepTask)) {
      if (!this.canEdit) return;
      this.selectLabel.emit(label.id);
      return;
    }
  }

  onRoiProtocolLabelSelected(label: Option): void {
    if (this.multiSelection) return;
    const pipelineId = this.getPipelineIdForLabelOption(label.id);
    if (!pipelineId || !this.canEdit || this.isRoiIncompatible(pipelineId)) return;
    this.selectLabel.emit(label.id);
  }

  private findCategoryContainingOption(optionId: string): Category | undefined {
    for (const id of Object.keys(this.protocolCategoriesById)) {
      const c = this.protocolCategoriesById[id];
      if (c.options?.includes(optionId)) return c;
    }
    return undefined;
  }

  private getPipelineIdForLabelOption(labelId: string): string {
    const byCategoryId = this.protocolCategoriesById;
    for (const p of (this.pipelines ?? []) as ProtocolAnalysisPipeline[]) {
      for (const cid of p.categories) {
        const cat = byCategoryId[cid];
        if (cat?.options?.includes(labelId)) return p.id;
      }
    }
    return '';
  }

  onRoiNewLabel(category: Category, pipelineId: string): void {
    this.newLabel.emit({ stepId: category.stepId, pipelineId });
  }

  onRoiNewLabelForCategoryId(categoryId: string): void {
    const category = this.protocolCategoriesById[categoryId];
    if (!category) return;
    this.onRoiNewLabel(category, this.getPipelineIdForCategory(categoryId));
  }

  getRoiAssetRoiCount(labelId: string): number {
    if (this.excludedFromCounter?.has(labelId)) return 0;
    return this.labelCount?.get(labelId) || 0;
  }

  getRoiSampleRoiCount(labelId: string): number {
    if (this.excludedFromCounter?.has(labelId)) return 0;
    return this.sampleLabelCount?.get(labelId) || 0;
  }

  getRoiDisplayAssetRoiCount(labelId: string): number {
    return this.labelCount?.get(labelId) || 0;
  }

  getRoiDisplaySampleRoiCount(labelId: string): number {
    return this.sampleLabelCount?.get(labelId) || 0;
  }

  getRoiSampleRoiPercentage(labelId: string): number {
    if (this.excludedFromCounter?.has(labelId)) return 0;
    if (this.totalCount <= 0) return 0;
    return (this.getRoiSampleRoiCount(labelId) / this.totalCount) * 100;
  }

  getRoiSampleRoiPercentageDisplay(labelId: string): string {
    if (this.excludedFromCounter?.has(labelId)) return '----%';
    if (this.totalCount <= 0) return '0%';
    const pct = this.getRoiSampleRoiPercentage(labelId);
    return pct > 0 ? pct.toFixed(1) + '%' : '0%';
  }

  getRoiAssetRoiPercentage(labelId: string): number {
    if (this.excludedFromCounter?.has(labelId)) return 0;
    if (this.totalActiveAssetRoiCount <= 0) return 0;
    return (this.getRoiAssetRoiCount(labelId) / this.totalActiveAssetRoiCount) * 100;
  }

  getRoiAssetRoiPercentageDisplay(labelId: string): string {
    if (this.excludedFromCounter?.has(labelId)) return '----%';
    if (this.totalActiveAssetRoiCount <= 0) return '0%';
    const pct = this.getRoiAssetRoiPercentage(labelId);
    return pct > 0 ? pct.toFixed(1) + '%' : '0%';
  }

  getRoiAssetCategoryTotalCount(category: Category): number {
    return this.assetCategoryCounts?.get(category.id) ?? 0;
  }

  getRoiSampleCategoryTotalCount(category: Category): number {
    let total = 0;
    for (const optionId of category.options) {
      if (this.excludedFromCounter.has(optionId)) continue;
      total += this.sampleLabelCount?.get(optionId) || 0;
    }
    return total;
  }

  getRoiAssetCategoryTotalPercentage(category: Category): number {
    if (this.totalActiveAssetRoiCount <= 0) return 0;
    return (this.getRoiAssetCategoryTotalCount(category) / this.totalActiveAssetRoiCount) * 100;
  }

  getRoiSampleCategoryTotalPercentage(category: Category): number {
    if (this.totalCount <= 0) return 0;
    return (this.getRoiSampleCategoryTotalCount(category) / this.totalCount) * 100;
  }

  isRoiIncompatible(pipelineId: string): boolean {
    return this.selectedLabels.some((label) => label.pipelineId !== pipelineId);
  }

  public onLabelHover(labelId: string): void {
    clearTimeout(this.hoverTimer);
    this.hoverTimer = setTimeout(() => this.hoverLabel.emit(labelId), SampleAnalysisPanelComponent.HOVER_DELAY_MS);
  }

  public onLabelHoverEnd(): void {
    clearTimeout(this.hoverTimer);
    this.hoverLabel.emit(null);
  }
}
